import React, { useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, FormControlLabel, MenuItem, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { WithholdingService } from '../../../services/withholding.service';
import { SupplierService } from '../../../services/supplier.service';
import { loadSuppliersSafely } from '../../../utils/supplierAccess';
import { AuthService } from '../../../services/auth.service';
import { PaymentWithholding, RegisterFilter } from '../../../interfaces/procurement/withholding.interface';
import { Supplier } from '../../../interfaces/procurement/supplier.interface';
import SearchableSelect from '../../common/SearchableSelect';
import { useToast } from '../../../context/ToastContext';
import { formatMoney } from '../../../utils/format';
import { formatDate, firstOfMonthLocalIso, toLocalIsoDate } from '../../../utils/dateUtils';
import { saveBlob } from '../../../utils/saveBlob';

export const WithholdingRegister: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!;
  const [filter, setFilter] = useState<RegisterFilter>({
    from: firstOfMonthLocalIso(), to: toLocalIsoDate(), type: '', supplierId: '', includeVoided: false,
  });
  const [rows, setRows] = useState<PaymentWithholding[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSuppliersSafely(() => SupplierService.listByInstitution(0, 500, 'ALL')).then(r => setSuppliers(r?.suppliers ?? []));
    run(); // eslint-disable-line @typescript-eslint/no-use-before-define
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    try {
      setLoading(true);
      setRows((await WithholdingService.register(institutionId, filter)) ?? []);
    } catch (e) {
      toast.showApiError(e, 'Failed to load the withholding register');
    } finally {
      setLoading(false);
    }
  };

  const exportAs = async (format: 'csv' | 'xlsx') => {
    try {
      const blob = await WithholdingService.exportRegister(institutionId, format, filter);
      saveBlob(blob, `withholding-register.${format}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to export the register');
    }
  };

  const openCertificate = async (r: PaymentWithholding) => {
    try {
      saveBlob(await WithholdingService.downloadCertificate(r.id), `${r.certificateNumber}.pdf`);
    } catch (e) {
      toast.showApiError(e, 'Failed to download the certificate');
    }
  };

  const total = (t: 'VDS' | 'TDS') =>
    rows.filter(r => r.type === t && r.status === 'ACTIVE').reduce((s, r) => s + r.amount, 0);

  return (
    <Box sx={{ p: 3 }} data-help-page="procurement.whRegister.page">
      <Typography variant="h5" sx={{ mb: 2 }}>Withholding register</Typography>
      <Paper sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }} data-help-section="procurement.whRegister.filter">
        <TextField data-help="procurement.whRegister.field.from" label="From" type="date" size="small" value={filter.from}
          onChange={e => setFilter({ ...filter, from: e.target.value })}
          InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 200 }} />
        <TextField data-help="procurement.whRegister.field.to" label="To" type="date" size="small" value={filter.to}
          onChange={e => setFilter({ ...filter, to: e.target.value })}
          InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 200 }} />
        <TextField data-help="procurement.whRegister.field.type" select label="Type" size="small" value={filter.type}
          onChange={e => setFilter({ ...filter, type: e.target.value as RegisterFilter['type'] })}
          sx={{ flex: '1 1 130px', maxWidth: 180 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="VDS">VDS</MenuItem>
          <MenuItem value="TDS">TDS</MenuItem>
        </TextField>
        <Box sx={{ flex: '2 1 220px', maxWidth: 320 }} data-help="procurement.whRegister.field.supplier">
          <SearchableSelect<Supplier> options={suppliers} value={filter.supplierId ?? ''}
            onChange={id => setFilter({ ...filter, supplierId: id })}
            getOptionLabel={s => s.name} label="Supplier" size="small" />
        </Box>
        <FormControlLabel label="Include voided" control={<Checkbox data-help="procurement.whRegister.field.includeVoided" checked={!!filter.includeVoided}
          onChange={e => setFilter({ ...filter, includeVoided: e.target.checked })} />} />
        <Button data-help="procurement.whRegister.run" variant="contained" onClick={run} disabled={loading}>Run</Button>
        <Button data-help="procurement.whRegister.csv" variant="outlined" onClick={() => exportAs('csv')}>CSV</Button>
        <Button data-help="procurement.whRegister.xlsx" variant="outlined" onClick={() => exportAs('xlsx')}>XLSX</Button>
      </Paper>
      {loading ? <CircularProgress /> : (
        <Paper sx={{ overflowX: 'auto' }} data-help-section="procurement.whRegister.table">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="procurement.whRegister.col.date">Date</TableCell><TableCell data-help="procurement.whRegister.col.payment">Payment</TableCell><TableCell data-help="procurement.whRegister.col.supplier">Supplier</TableCell>
                <TableCell data-help="procurement.whRegister.col.tax">BIN/TIN</TableCell><TableCell data-help="procurement.whRegister.col.bill">Bill</TableCell><TableCell data-help="procurement.whRegister.col.type">Type</TableCell>
                <TableCell data-help="procurement.whRegister.col.base" align="right">Base</TableCell><TableCell data-help="procurement.whRegister.col.rate" align="right">Rate</TableCell>
                <TableCell data-help="procurement.whRegister.col.amount" align="right">Amount</TableCell><TableCell data-help="procurement.whRegister.col.cert">Certificate</TableCell>
                <TableCell data-help="procurement.whRegister.col.challan">Challan</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={11}>No withholdings for this selection.</TableCell></TableRow>}
              {rows.map(r => (
                <TableRow key={r.id} sx={r.status === 'VOIDED' ? { opacity: 0.55 } : undefined}>
                  <TableCell>{formatDate(r.paymentDate)}</TableCell>
                  <TableCell>{r.paymentNumber}</TableCell>
                  <TableCell>{r.supplierName}</TableCell>
                  <TableCell>{r.supplierTaxNo ?? '—'}</TableCell>
                  <TableCell>{r.billInvoiceRef || r.billNumber}</TableCell>
                  <TableCell>{r.type}</TableCell>
                  <TableCell align="right">{formatMoney(r.baseAmount)}</TableCell>
                  <TableCell align="right">{r.ratePct}%</TableCell>
                  <TableCell align="right">{formatMoney(r.amount)}</TableCell>
                  <TableCell>
                    <Button data-help="procurement.whRegister.action.cert" size="small" onClick={() => openCertificate(r)}>{r.certificateNumber}</Button>
                    {r.status === 'VOIDED' && ' (void)'}
                  </TableCell>
                  <TableCell>{r.challanNumber ? `${r.challanNumber} (${formatDate(r.depositDate!)})` : 'Not deposited'}</TableCell>
                </TableRow>
              ))}
              {rows.length > 0 && (
                <TableRow>
                  <TableCell colSpan={8}><b>Totals (active)</b></TableCell>
                  <TableCell align="right" colSpan={3}>
                    <b>VDS {formatMoney(total('VDS'))} / TDS {formatMoney(total('TDS'))}</b>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default WithholdingRegister;
